import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Plus, Trash2, Save, X } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

const BLANK = { label: "Home", line1: "", line2: "", city: "", state: "FL", zip: "", country: "US", is_default: false, notes: "" };

export default function AddressesPage() {
  const [addresses, setAddresses] = useState([]);
  const [editing, setEditing] = useState(null);
  useDocumentTitle("Saved addresses");

  const load = async () => {
    try { const { data } = await api.get("/addresses"); setAddresses(data || []); } catch (_) {}
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/addresses/${editing.id}`, editing);
      else await api.post("/addresses", editing);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.error || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this address?")) return;
    try { await api.delete(`/addresses/${id}`); load(); } catch (err) { toast.error("Failed"); }
  };

  return (
    <DashboardShell>
      <div className="max-w-3xl" data-testid="addresses-page">
        <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Account</div>
            <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Saved addresses</h1>
            <p className="text-[#252A34]/70 text-sm mt-1">Use these to speed up booking checkout.</p>
          </div>
          <button onClick={() => setEditing({ ...BLANK })} className="inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium" data-testid="address-new">
            <Plus className="h-4 w-4" /> Add address
          </button>
        </div>

        {addresses.length === 0 && !editing ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <MapPin className="h-6 w-6 mx-auto text-[#165a71]" />
            <div className="mt-2 font-display text-lg text-[#071b22] font-semibold">No addresses saved.</div>
            <div className="text-sm text-[#252A34]/70 mt-1">Add one to speed up booking checkout.</div>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="addresses-list">
            {addresses.map((a) => (
              <li key={a.id} className="rounded-2xl border border-slate-200 bg-white p-4 flex items-start gap-3" data-testid={`address-row-${a.id}`}>
                <div className="h-10 w-10 rounded-xl bg-[#165a71]/10 text-[#165a71] flex items-center justify-center">
                  <MapPin className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-[#071b22]">{a.label}{a.is_default && <span className="ml-2 text-[10px] uppercase tracking-wider bg-[#165a71]/10 text-[#165a71] rounded-full px-2 py-0.5 font-medium">default</span>}</div>
                  <div className="text-sm text-[#252A34]/80">{a.line1}{a.line2 ? `, ${a.line2}` : ""}</div>
                  <div className="text-xs text-[#252A34]/60">{a.city}, {a.state} {a.zip}</div>
                </div>
                <div className="flex flex-col gap-2">
                  <button onClick={() => setEditing({ ...a })} className="text-xs font-medium text-[#165a71]" data-testid={`address-edit-${a.id}`}>Edit</button>
                  <button onClick={() => remove(a.id)} className="text-xs text-rose-600" data-testid={`address-delete-${a.id}`}>Delete</button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {editing && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#071b22]/50 p-4">
            <div className="w-full max-w-lg rounded-3xl bg-white p-6 border border-slate-200" data-testid="address-form">
              <div className="flex items-start justify-between mb-3">
                <div className="font-display text-lg font-semibold text-[#071b22]">{editing.id ? "Edit address" : "New address"}</div>
                <button onClick={() => setEditing(null)} className="rounded p-1 hover:bg-slate-100"><X className="h-4 w-4" /></button>
              </div>
              <div className="grid gap-3">
                {[["label", "Label"], ["line1", "Street address"], ["line2", "Apt / Suite (optional)"], ["city", "City"], ["state", "State"], ["zip", "ZIP"], ["notes", "Notes"]].map(([k, l]) => (
                  <div key={k}>
                    <label className="text-xs text-[#252A34]/70">{l}</label>
                    <input value={editing[k] || ""} onChange={(e) => setEditing((p) => ({ ...p, [k]: e.target.value }))} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid={`address-field-${k}`} />
                  </div>
                ))}
                <label className="inline-flex items-center gap-2 text-sm text-[#252A34] mt-1">
                  <input type="checkbox" checked={!!editing.is_default} onChange={(e) => setEditing((p) => ({ ...p, is_default: e.target.checked }))} data-testid="address-default" />
                  Set as default address
                </label>
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <button onClick={() => setEditing(null)} className="rounded-full border border-slate-200 px-4 py-2 text-sm">Cancel</button>
                <button onClick={save} className="inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium" data-testid="address-save">
                  <Save className="h-4 w-4" /> Save
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
